import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useSystemRoles } from '../../hooks/useSystemRoles';
import { useUpdateUserRole } from '../../hooks/useUpdateUserRole';
import { usePermissions } from '../../hooks/usePermissions';
import { Button } from '../ui/Button';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    user_id: string;
    company_id: string;
    role: string;
    is_active: boolean | null;
  };
  onSuccess?: () => void;
}

/**
 * Modal for editing user settings (role, status)
 * Role restrictions:
 * - RollOps Admin: Can assign any role
 * - Owner: Can assign up to owner (max: owner)
 * - Gym Admin: Can assign up to admin (max: admin, cannot assign owner)
 * - Coach/Reception: Cannot change roles (modal not shown for them)
 */
export function UserSettingsModal({
  isOpen,
  onClose,
  user,
  onSuccess,
}: UserSettingsModalProps) {
  const permissions = usePermissions();
  const systemRolesQuery = useSystemRoles();
  const updateRoleMutation = useUpdateUserRole();

  const [editedRole, setEditedRole] = useState(user.role);
  const [editedIsActive, setEditedIsActive] = useState(user.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (isOpen) {
      // Ensure we use the actual user role, not a default
      const actualRole = user.role || 'student';
      setEditedRole(actualRole);
      setEditedIsActive(user.is_active ?? true);
      setError(null);
    }
  }, [isOpen, user]);

  // Determine max role user can assign based on their own role
  const getMaxAssignableRole = (): string => {
    // RollOps Admin (system admin) can assign any role
    if (permissions.isAdmin || permissions.isDeveloper) {
      return 'owner';
    }
    // Owner can assign up to owner
    if (permissions.role === 'owner') {
      return 'owner';
    }
    // Gym Admin can assign up to admin (NOT owner - only owners can assign owner)
    if (permissions.role === 'admin') {
      return 'admin';
    }
    // Others cannot assign roles
    return 'student';
  };

  // Filter available roles based on user's permissions
  const getAvailableRoles = () => {
    if (!systemRolesQuery.data) return [];
    
    const maxRole = getMaxAssignableRole();
    // Role hierarchy: lower index = higher privilege
    // owner(0) > admin(1) > coach(2) > reception(3) > student(4)
    const roleHierarchy = ['owner', 'admin', 'coach', 'reception', 'student'];
    const maxIndex = roleHierarchy.indexOf(maxRole);
    
    if (maxIndex === -1) {
      // If maxRole not found in hierarchy, return all roles (shouldn't happen)
      console.warn('[UserSettingsModal] Max role not found in hierarchy:', maxRole);
      return systemRolesQuery.data;
    }
    
    // Return roles that are at or BELOW the max role in the hierarchy
    // Owner (index 0) can assign: owner(0), admin(1), coach(2), reception(3), student(4) - all roles (index >= 0)
    // Gym Admin (index 1) can assign: admin(1), coach(2), reception(3), student(4) - roles with index >= 1
    // So we want: roleIndex >= maxIndex (roles at or below max role)
    return systemRolesQuery.data.filter(role => {
      const roleIndex = roleHierarchy.indexOf(role.role);
      // Include roles that exist in hierarchy and are at or below max role (higher or equal index)
      return roleIndex !== -1 && roleIndex >= maxIndex;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Validation: Gym Admin cannot assign owner role
      if (permissions.role === 'admin' && editedRole === 'owner') {
        throw new Error('Gym Admins cannot assign the Owner role. Only Owners can assign Owner role.');
      }

      // Update role if changed
      if (editedRole !== user.role) {
        await updateRoleMutation.mutateAsync({
          userId: user.user_id,
          companyId: user.company_id,
          newRole: editedRole,
        });
      }

      // Update is_active status if changed (on user_profiles, not user_company_relationships)
      if (editedIsActive !== user.is_active) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: statusError } = await (supabase as any)
          .from('user_profiles')
          .update({ is_active: editedIsActive })
          .eq('id', user.user_id);

        if (statusError) {
          throw new Error(`Failed to update status: ${statusError.message}`);
        }
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('[UserSettingsModal] Error saving:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const availableRoles = getAvailableRoles();
  const maxRole = getMaxAssignableRole();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#EBFFEE]">User Settings</h2>
          <button
            onClick={onClose}
            className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-600/50 rounded text-text-primary text-sm">
            {error}
          </div>
        )}

        {/* Role Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Role
            {maxRole !== 'owner' && (
              <span className="ml-2 text-xs text-[#A9A3A3]">
                (Max: {maxRole === 'admin' ? 'Gym Admin' : maxRole})
              </span>
            )}
          </label>
          {systemRolesQuery.isLoading ? (
            <div className="text-[#A9A3A3] text-sm">Loading roles...</div>
          ) : availableRoles.length === 0 ? (
            <div className="text-red-400 text-sm">No roles available. Please check permissions.</div>
          ) : (
            <>
              <select
                value={editedRole}
                onChange={(e) => {
                  const newRole = e.target.value;
                  // Prevent gym admin from selecting owner role
                  if (permissions.role === 'admin' && newRole === 'owner') {
                    return; // Don't allow the change
                  }
                  setEditedRole(newRole);
                }}
                disabled={saving || updateRoleMutation.isPending}
                className="w-full px-3 py-2 bg-[#363232] text-[#EBFFEE] border border-[#504A4A] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9B1421] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {availableRoles.map((role) => {
                  // Show clear labels to avoid confusion
                  let displayName = role.role;
                  if (role.role === 'admin') {
                    displayName = 'Gym Admin';
                  } else if (role.role === 'owner') {
                    displayName = 'Owner';
                  }
                  return (
                    <option key={role.system_role_id} value={role.role}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
              {/* Debug info in development */}
              {process.env.NODE_ENV === 'development' && (
                <p className="mt-1 text-xs text-[#A9A3A3]">
                  Debug: Current role={user.role}, Available roles={availableRoles.map(r => r.role).join(', ')}
                </p>
              )}
              {permissions.role === 'admin' && (
                <p className="mt-1 text-xs text-[#A9A3A3]">
                  Note: Gym Admins can assign up to Gym Admin role. Only Owners can assign Owner role.
                </p>
              )}
            </>
          )}
        </div>

        {/* Status Toggle */}
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editedIsActive}
              onChange={(e) => setEditedIsActive(e.target.checked)}
              disabled={saving}
              className="w-4 h-4 rounded border-[#504A4A] bg-[#363232] text-text-primary focus:ring-[#9B1421] disabled:opacity-50"
            />
            <span className="text-sm text-[#EBFFEE]">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            disabled={saving}
            className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || updateRoleMutation.isPending}
            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2"
          >
            {saving || updateRoleMutation.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
